import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Button,
  Stepper, Step, StepLabel, Chip, Avatar, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Paper, Divider, IconButton,
} from '@mui/material';
import NavigationIcon from '@mui/icons-material/Navigation';
import CallIcon from '@mui/icons-material/Call';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import restaurantService from '../../services/restaurant.service';
import realtimeService from '../../services/realtime.service';

const steps = ['Acceptée', 'En route récupération', 'Récupéré', 'En route livraison', 'Livré'];

const ActiveDelivery = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [deliveryData, setDeliveryData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [proofDialog, setProofDialog] = useState(false);
  const [deliveryCode, setDeliveryCode] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [currentPosition, setCurrentPosition] = useState(null);
  const [gpsActive, setGpsActive] = useState(false);
  const [autoDeliveringCountdown, setAutoDeliveringCountdown] = useState(null);
  const watchIdRef = useRef(null);
  const autoDeliveringRef = useRef(null);
  const deliveryDataRef = useRef(null);
  const activeStepRef = useRef(1);

  // Garder les refs synchronisees avec le state pour le callback GPS
  useEffect(() => { deliveryDataRef.current = deliveryData; }, [deliveryData]);
  useEffect(() => { activeStepRef.current = activeStep; }, [activeStep]);

  useEffect(() => {
    loadDeliveryData();
    realtimeService.connect();
    if (id) realtimeService.joinOrder(id);
    startLocationTracking();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      clearInterval(autoDeliveringRef.current);
      if (id) realtimeService.leaveOrder(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDeliveryData = async () => {
    try {
      const data = await restaurantService.getOrder(id);
      const order = data.order || data;

      // Commande annulee ou terminee -> rediriger
      if (order.order_status === 'cancelled') {
        setDeliveryData({ ...order, cancelled: true });
        return;
      }
      if (order.order_status === 'delivered' || order.order_status === 'collected') {
        navigate('/partner/delivery');
        return;
      }

      // Map order_status to step index
      const statusToStep = {
        confirmed: 1,
        preparing: 1,
        ready: 1,
        picked_up: 2,
        delivering: 3,
        delivered: 4,
      };
      const step = statusToStep[order.order_status] ?? 1;

      setActiveStep(step);

      // Si picked_up, calculer le temps restant avant auto-delivering
      const pickupTime = order.picked_up_at || order.delivery_accepted_at;
      if (order.order_status === 'picked_up' && pickupTime) {
        const pickedAt = new Date(pickupTime).getTime();
        const elapsed = Math.floor((Date.now() - pickedAt) / 1000);
        const remaining = 60 - elapsed;
        if (remaining > 0) {
          startAutoDeliveringTimer(remaining);
        } else {
          // Deja depasse 60s, passer directement en delivering
          restaurantService.updateOrderStatus(order.id, 'delivering').catch(() => {});
          setActiveStep(3);
          realtimeService.sendStatusUpdate(String(order.id), 'delivering');
        }
      }

      setDeliveryData({
        id: order.id,
        type: 'restaurant',
        order_number: order.order_number,
        order_status: order.order_status,
        restaurant_name: order.restaurant_name,
        customer_id: order.customer_id,
        customer_name: order.customer_name || `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() || 'Client',
        customer_phone: order.customer_phone || '',
        pickup_address: order.restaurant_address || 'Adresse restaurant',
        pickup_lat: order.restaurant_lat || -12.8490,
        pickup_lng: order.restaurant_lng || 45.0996,
        delivery_address: order.delivery_address || 'Adresse livraison',
        delivery_lat: order.delivery_lat || -12.8490,
        delivery_lng: order.delivery_lng || 45.0996,
        items: order.items || [],
        delivery_fee: order.delivery_fee || 0,
        total_amount: order.total_amount || 0,
        special_instructions: order.special_instructions || '',
        verification_code: order.delivery_code || null,
      });
    } catch (err) {
      setDeliveryData(null);
    }
  };

  const startLocationTracking = () => {
    if (!navigator.geolocation) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const pos = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          heading: position.coords.heading || 0,
          speed: position.coords.speed || 0,
        };
        setCurrentPosition(pos);
        setGpsActive(true);

        if (id) {
          realtimeService.sendPosition(id, pos);
        }

        if (deliveryDataRef.current && activeStepRef.current >= 3) {
          const dd = deliveryDataRef.current;
          const dist = getDistance(pos.latitude, pos.longitude, dd.delivery_lat, dd.delivery_lng);
          const etaMin = Math.max(1, Math.round(dist / 0.5));
          realtimeService.sendEtaUpdate(id, etaMin, dist.toFixed(1));
        }
      },
      () => {
        setGpsActive(false);
      },
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 10000 }
    );
  };

  const getDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const handleNextStep = async () => {
    if (activeStep === 1) {
      // Arriving at restaurant -> confirm pickup dialog
      setConfirmDialog(true);
    } else if (activeStep === 3) {
      // At client -> confirm delivery with code
      setProofDialog(true);
    } else if (activeStep === 2) {
      // Picked up -> en route to client (manual)
      clearInterval(autoDeliveringRef.current);
      setAutoDeliveringCountdown(null);
      try {
        if (id) {
          await restaurantService.updateOrderStatus(id, 'delivering');
        }
        setActiveStep(3);
        realtimeService.sendStatusUpdate(id || deliveryData?.id, 'delivering', deliveryData?.customer_id);
      } catch {
        // silent
      }
    }
  };

  const handleConfirmPickup = async () => {
    setConfirmDialog(false);
    try {
      if (id) {
        await restaurantService.updateOrderStatus(id, 'picked_up');
      }
      setActiveStep(2);
      realtimeService.sendStatusUpdate(id || deliveryData?.id, 'picked_up', deliveryData?.customer_id);
      // Lancer le timer auto-delivering (60s)
      startAutoDeliveringTimer(60);
    } catch {
      // silent
    }
  };

  // -- Auto-switch to "delivering" 60s after picked_up --
  const startAutoDeliveringTimer = (seconds) => {
    clearInterval(autoDeliveringRef.current);
    setAutoDeliveringCountdown(seconds);
    autoDeliveringRef.current = setInterval(() => {
      setAutoDeliveringCountdown(prev => {
        if (prev <= 1) {
          clearInterval(autoDeliveringRef.current);
          // Passer automatiquement en "delivering"
          if (id) {
            restaurantService.updateOrderStatus(id, 'delivering').catch(() => {});
          }
          setActiveStep(3);
          realtimeService.sendStatusUpdate(id, 'delivering');
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleConfirmDelivery = async () => {
    setError('');
    try {
      const response = await restaurantService.verifyDeliveryCode(id, deliveryCode);

      if (response.success === false) {
        setError(response.message || 'Code de vérification incorrect');
        return;
      }

      setProofDialog(false);
      setActiveStep(4);
      realtimeService.sendStatusUpdate(id || deliveryData?.id, 'delivered', deliveryData?.customer_id);
      setTimeout(() => navigate('/partner/delivery'), 3000);
    } catch (err) {
      setError('Code de vérification incorrect');
    }
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
  };

  if (!deliveryData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (deliveryData.cancelled) {
    return (
      <Box sx={{ minHeight: '100vh', pb: 4, bgcolor: '#f5f5f5' }}>
        <Container maxWidth="lg" sx={{ pt: 6, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Cette commande a été annulée.
          </Alert>
          <Button variant="contained" onClick={() => navigate('/partner/delivery')}>
            Retour au tableau de bord
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 4, bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        color: 'white',
        py: 3,
      }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/partner/delivery')} sx={{ color: 'white' }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" fontWeight={700}>Livraison en cours</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  #{deliveryData.order_number}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%',
                bgcolor: gpsActive ? '#4caf50' : '#e74c3c',
                animation: gpsActive ? 'pulse-glow 1.5s ease-in-out infinite' : 'none',
                '@keyframes pulse-glow': {
                  '0%, 100%': { boxShadow: '0 0 4px rgba(76,175,80,0.5)' },
                  '50%': { boxShadow: '0 0 12px rgba(76,175,80,0.8)' },
                },
              }} />
              <Typography variant="caption" sx={{ color: gpsActive ? '#4caf50' : '#e74c3c', fontWeight: 600 }}>
                {gpsActive ? 'GPS Actif' : 'GPS Inactif'}{/* corrige */}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {/* Position actuelle */}
        {currentPosition && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <MyLocationIcon sx={{ color: '#3498db' }} />
              <Box flex={1}>
                <Typography variant="body2" color="text.secondary">Position actuelle</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {currentPosition.latitude.toFixed(5)}, {currentPosition.longitude.toFixed(5)}
                  {currentPosition.speed > 0 && ` -- ${(currentPosition.speed * 3.6).toFixed(0)} km/h`}
                </Typography>
              </Box>
              <Chip label="En direct" size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
            </CardContent>
          </Card>
        )}

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{
            '& .MuiStepIcon-root.Mui-completed': { color: '#2D4A35' },
            '& .MuiStepIcon-root.Mui-active': { color: '#F57C00' },
          }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Paper>

        {activeStep === 4 && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Livraison terminee avec succes ! Retour au tableau de bord...
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="start" mb={3}>
              <Box>
                <Chip label="Restaurant" size="small" color="primary" sx={{ mb: 1 }} />
                <Typography variant="h6" fontWeight={600}>{deliveryData.restaurant_name}</Typography>
              </Box>
              <Chip
                label={`${(parseFloat(deliveryData.delivery_fee) * 0.95).toFixed(2)} EUR`}
                sx={{
                  fontWeight: 700, fontSize: '1rem',
                  bgcolor: 'rgba(76,175,80,0.1)', color: '#2e7d32',
                  border: '1px solid rgba(76,175,80,0.3)',
                }}
              />
            </Box>

            {/* Point de recuperation */}
            {activeStep <= 1 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(52,152,219,0.06)', borderRadius: 2, border: '1px solid rgba(52,152,219,0.2)' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationOnIcon sx={{ color: '#3498db' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Point de recuperation</Typography>
                </Box>
                <Typography variant="body1" gutterBottom>
                  {deliveryData.pickup_address}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<NavigationIcon />}
                  onClick={() => openGoogleMaps(deliveryData.pickup_lat, deliveryData.pickup_lng)}
                  sx={{ mt: 1, background: 'linear-gradient(135deg, #3498db, #2980b9)' }}
                >
                  Itineraire
                </Button>
              </Box>
            )}

            {/* Point de livraison */}
            {activeStep >= 2 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(45,74,53,0.06)', borderRadius: 2, border: '1px solid rgba(45,74,53,0.2)' }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationOnIcon sx={{ color: '#2D4A35' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Adresse de livraison</Typography>
                </Box>
                <Typography variant="body1" gutterBottom>
                  {deliveryData.delivery_address}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<NavigationIcon />}
                  onClick={() => openGoogleMaps(deliveryData.delivery_lat, deliveryData.delivery_lng)}
                  sx={{ mt: 1, background: 'linear-gradient(135deg, #2D4A35, #1E3525)' }}
                >
                  Itineraire
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Infos client */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Client</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: '#552583', color: 'white' }}>
                  {deliveryData.customer_name.charAt(0)}
                </Avatar>
                <Box flexGrow={1}>
                  <Typography variant="body1" fontWeight={500}>{deliveryData.customer_name}</Typography>
                  <Typography variant="body2" color="text.secondary">{deliveryData.customer_phone}</Typography>
                </Box>
                {deliveryData.customer_phone && (
                  <IconButton
                    href={`tel:${deliveryData.customer_phone}`}
                    sx={{ bgcolor: 'rgba(52,152,219,0.1)', color: '#3498db' }}
                  >
                    <CallIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            {/* Articles */}
            {deliveryData.items?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Articles</Typography>
                {deliveryData.items.map((item, index) => (
                  <Box key={index} sx={{ py: 0.5 }}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">{item.name || item.menu_item_name}</Typography>
                      <Typography variant="body2" color="text.secondary">x{item.quantity}</Typography>
                    </Box>
                    {Array.isArray(item.options_chosen) && item.options_chosen.length > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', pl: 1, lineHeight: 1.5 }}>
                        {item.options_chosen.map(g => `${g.group_name}: ${g.choices.map(c => c.name).join(', ')}`).join(' • ')}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {deliveryData.special_instructions && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Instructions</Typography>
                <Typography variant="body2">{deliveryData.special_instructions}</Typography>
              </Alert>
            )}

            {activeStep < 4 && (
              <Button
                fullWidth variant="contained" size="large"
                onClick={handleNextStep}
                startIcon={activeStep === 3 ? <CheckCircleIcon /> : <NavigationIcon />}
                sx={{
                  py: 2,
                  background: activeStep === 3
                    ? 'linear-gradient(135deg, #2D4A35, #1E3525)'
                    : 'linear-gradient(135deg, #3498db, #2980b9)',
                  fontSize: '1rem',
                  fontWeight: 700,
                }}
              >
                {activeStep === 1 && "J'ai recupere la commande"}
                {activeStep === 2 && (autoDeliveringCountdown ? `En route vers le client (auto ${autoDeliveringCountdown}s)` : "En route vers le client")}
                {activeStep === 3 && "Confirmer la livraison"}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Dialog confirmation recuperation */}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmer la recuperation</DialogTitle>
          <DialogContent>
            <Typography gutterBottom color="text.secondary">
              Avez-vous bien recupere tous les articles ?
            </Typography>
            <TextField fullWidth multiline rows={2} label="Notes (optionnel)" value={notes}
              onChange={(e) => setNotes(e.target.value)} sx={{ mt: 2 }} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDialog(false)}>Annuler</Button>
            <Button onClick={handleConfirmPickup} variant="contained"
              sx={{ background: 'linear-gradient(135deg, #2D4A35, #1E3525)' }}>Confirmer</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog preuve de livraison */}
        <Dialog open={proofDialog} onClose={() => setProofDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Preuve de livraison</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
              Demandez le code de verification au client
            </Typography>
            <TextField fullWidth label="Code de verification" value={deliveryCode}
              onChange={(e) => setDeliveryCode(e.target.value)}
              placeholder="Code a 6 chiffres" sx={{ mb: 3 }} inputProps={{ maxLength: 6 }} />
            <TextField fullWidth multiline rows={3} label="Notes de livraison (optionnel)"
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setProofDialog(false)}>Annuler</Button>
            <Button onClick={handleConfirmDelivery} variant="contained"
              sx={{ background: 'linear-gradient(135deg, #2D4A35, #1E3525)' }}>Confirmer la livraison</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ActiveDelivery;
